import React from "react";
import { IUserRef } from "../../models/userData.model";
import {
  IRepetitiveTaskManager,
  ITaskManagement,
  RepetitiveTaskDays,
  RepetitiveTaskType,
} from "../../models/taskManagement.model";
import UserSelectorSearch from "../Users/UserSelectorSearch";
import { getUserId } from "../../utils/auth";
import Button, { ButtonType } from "../../assets/Button";
import TiptapEditor from "../Assets/TiptapEditor";
type FormChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
>;

interface Props {
  currentTask: ITaskManagement | IRepetitiveTaskManager | null;
  editMode: boolean;
  setEditMode: (v: boolean) => void;
  form: ITaskManagement;
  setForm: React.Dispatch<React.SetStateAction<ITaskManagement>>;
  handleChange: (e: FormChangeEvent) => void;
  handleUpdateTask: () => void;
  handleCancelEdit: () => void;
  inputBaseClasses: string;
  selectClasses: string;
  labelClasses: string;
  types: Record<string, string> | any;
  priorities: Record<string, string> | any;
  users: IUserRef[];
  getStatusColor: (status: string) => string;
  getPriorityColor: (priority: string) => string;
  formatDate: (d: Date | string | null) => string;
  formatDateForInput: (d?: Date | string) => string;
  navigate: (to: string) => void;
  repetitionType: RepetitiveTaskType;
  handleRepetitionTypeChange: (type: RepetitiveTaskType) => void;
  selectedDays: RepetitiveTaskDays[];
  handleDaySelection: (day: RepetitiveTaskDays) => void;
  handleDaySelectionAll: (select: boolean) => void;
  monthlyDate: string;
  setMonthlyDate: (date: string) => void;
  yearlyDate: string;
  setYearlyDate: (date: string) => void;
  noOfDays: number;
  setNoOfDays: (days: number) => void;
  onOpenCompleteModal: () => void;
  handleStopRepetitiveTask: () => void;
}

const TaskDetailsPanel: React.FC<Props> = ({
  currentTask,
  editMode,
  setEditMode,
  form,
  setForm,
  handleChange,
  handleUpdateTask,
  handleCancelEdit,
  inputBaseClasses,
  selectClasses,
  labelClasses,
  types,
  priorities,
  users,
  getStatusColor,
  getPriorityColor,
  formatDate,
  formatDateForInput,
  navigate,
  repetitionType,
  handleRepetitionTypeChange,
  selectedDays,
  handleDaySelection,
  handleDaySelectionAll,
  monthlyDate,
  setMonthlyDate,
  yearlyDate,
  setYearlyDate,
  noOfDays,
  setNoOfDays,
  onOpenCompleteModal,
  handleStopRepetitiveTask,
}) => {
  const formatRepetitionDetails = (
    repetition: IRepetitiveTaskManager["typeOfRepetition"] | undefined
  ) => {
    if (!repetition) return "No repetition details available.";

    switch (repetition.type) {
      case RepetitiveTaskType.weekly:
        return `Occurs every week on ${repetition.data?.days?.join(", ")}`;
      case RepetitiveTaskType.monthly:
        return `Occurs monthly on ${repetition.data?.date}`;
      case RepetitiveTaskType.yearly:
        return `Occurs every year on ${repetition.data?.date}`;
      default:
        return "Custom repetition pattern";
    }
  };

  const getAddedDatesEntries = (): [string, string][] => {
    const ad = (currentTask as any)?.addedDates;
    if (!ad) return [];

    if (ad instanceof Map) {
      return Array.from(ad.entries()) as [string, string][];
    }

    if (typeof ad === "object") {
      return Object.entries(ad) as [string, string][];
    }

    return [];
  };

  const isCurrentUserAssignee = () => {
    if (!currentTask) return false;
    const assigneeId = (currentTask.assignee as unknown as IUserRef)?._id;
    return assigneeId === getUserId();
  };

  const canCompleteTask = () => {
    return (
      currentTask?.status === "ongoing" && isCurrentUserAssignee() && !editMode
    );
  };

  const addedDatesEntries = getAddedDatesEntries();

  return (
    <div className="bg-white rounded-xl shadow-xs border border-gray-200">
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <svg
            onClick={() => navigate("/")}
            className="w-4 h-4 cursor-pointer"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>

          {editMode ? (
            <input
              name="taskName"
              value={form.taskName || ""}
              onChange={handleChange}
              placeholder="Enter task name"
              className="w-full font-bold p-2 border border-gray-200 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 outline-none bg-white text-gray-900 placeholder-gray-500"
            />
          ) : (
            <h1 className="text-lg font-bold text-gray-900">
              {currentTask?.taskName?.capitalizeWords?.() ??
                currentTask?.taskName}
            </h1>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div
            className={`text-sm font-medium ${getStatusColor(
              currentTask?.status ?? ""
            )}`}
          >
            {currentTask?.status?.capitalizeWords?.() ?? currentTask?.status}
          </div>

          <div className="flex items-center gap-2">
            {!editMode ? (
              <svg
                onClick={() => setEditMode(true)}
                className="cursor-pointer hover:scale-95 duration-300"
                width="16px"
                height="16px"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  fill="#1C274C"
                  d="M11.9426 1.25L13.5 1.25C13.9142 1.25 14.25 1.58579 14.25 2C14.25 2.41421 13.9142 2.75 13.5 2.75H12C9.62178 2.75 7.91356 2.75159 6.61358 2.92637C5.33517 3.09825 4.56445 3.42514 3.9948 3.9948C3.42514 4.56445 3.09825 5.33517 2.92637 6.61358C2.75159 7.91356 2.75 9.62178 2.75 12C2.75 14.3782 2.75159 16.0864 2.92637 17.3864C3.09825 18.6648 3.42514 19.4355 3.9948 20.0052C4.56445 20.5749 5.33517 20.9018 6.61358 21.0736C7.91356 21.2484 9.62178 21.25 12 21.25C14.3782 21.25 16.0864 21.2484 17.3864 21.0736C18.6648 20.9018 19.4355 20.5749 20.0052 20.0052C20.5749 19.4355 20.9018 18.6648 21.0736 17.3864C21.2484 16.0864 21.25 14.3782 21.25 12V10.5C21.25 10.0858 21.5858 9.75 22 9.75C22.4142 9.75 22.75 10.0858 22.75 10.5V12.0574C22.75 14.3658 22.75 16.1748 22.5603 17.5863C22.366 19.031 21.9607 20.1711 21.0659 21.0659C20.1711 21.9607 19.031 22.366 17.5863 22.5603C16.1748 22.75 14.3658 22.75 12.0574 22.75H11.9426C9.63423 22.75 7.82519 22.75 6.41371 22.5603C4.96897 22.366 3.82895 21.9607 2.93414 21.0659C2.03933 20.1711 1.63399 19.031 1.43975 17.5863C1.24998 16.1748 1.24999 14.3658 1.25 12.0574V11.9426C1.24999 9.63423 1.24998 7.82519 1.43975 6.41371C1.63399 4.96897 2.03933 3.82895 2.93414 2.93414C3.82895 2.03933 4.96897 1.63399 6.41371 1.43975C7.82519 1.24998 9.63423 1.24999 11.9426 1.25ZM16.7705 2.27592C18.1384 0.908029 20.3562 0.908029 21.7241 2.27592C23.092 3.6438 23.092 5.86158 21.7241 7.22947L15.076 13.8776C14.7047 14.2489 14.4721 14.4815 14.2126 14.684C13.9069 14.9224 13.5761 15.1268 13.2261 15.2936C12.929 15.4352 12.6169 15.5392 12.1188 15.7052L9.21426 16.6734C8.67801 16.8521 8.0868 16.7126 7.68711 16.3129C7.28742 15.9132 7.14785 15.322 7.3266 14.7857L8.29477 11.8812C8.46079 11.3831 8.56479 11.071 8.7064 10.7739C8.87319 10.4239 9.07761 10.0931 9.31605 9.78742C9.51849 9.52787 9.7511 9.29529 10.1224 8.924L16.7705 2.27592ZM20.6634 3.33658C19.8813 2.55448 18.6133 2.55448 17.8312 3.33658L17.4546 3.7132C17.4773 3.80906 17.509 3.92327 17.5532 4.05066C17.6965 4.46372 17.9677 5.00771 18.48 5.51999C18.9923 6.03227 19.5363 6.30346 19.9493 6.44677C20.0767 6.49097 20.1909 6.52273 20.2868 6.54543L20.6634 6.16881C21.4455 5.38671 21.4455 4.11867 20.6634 3.33658ZM19.1051 7.72709C18.5892 7.50519 17.9882 7.14946 17.4193 6.58065C16.8505 6.01185 16.4948 5.41082 16.2729 4.89486L11.2175 9.95026C10.801 10.3668 10.6376 10.532 10.4988 10.7099C10.3274 10.9297 10.1804 11.1676 10.0605 11.4192C9.96337 11.623 9.88868 11.8429 9.7024 12.4017L9.27051 13.6974L10.3026 14.7295L11.5983 14.2976C12.1571 14.1113 12.377 14.0366 12.5808 13.9395C12.8324 13.8196 13.0703 13.6726 13.2901 13.5012C13.468 13.3624 13.6332 13.199 14.0497 12.7825L19.1051 7.72709Z"
                />
              </svg>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-all"
                  aria-label="Cancel edit"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-gray-600 group-hover:text-gray-700"
                  >
                    <path
                      d="M15 9L9 15M9 9L15 15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleUpdateTask}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 transition-all"
                  aria-label="Save changes"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-indigo-600"
                  >
                    <path
                      d="M6 2C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2H6Z"
                      fill="currentColor"
                    />
                    <path
                      d="M14 2V8H20"
                      fill="none"
                      stroke="white"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M12 11V17M9 14L12 17L15 14"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Save
                </button>
              </>
            )}
            {currentTask?.typeOfRepetition && !currentTask.isCompleted && (
              <button
                onClick={handleStopRepetitiveTask}
                className="p-2 rounded-full inline-flex items-center justify-center hover:bg-red-100 transition-all"
                aria-label="Stop repetitive task"
                title="Stop Repetitive Task"
              >
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
                  />
                </svg>
              </button>
            )}
          </div>
          {canCompleteTask() && (
            <Button
              onClick={onOpenCompleteModal}
              type={ButtonType.Extra}
              className="text-xs"
              aria-label="Mark complete"
            >
              Mark as complete
            </Button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-2">
              Task Type
            </label>
            {editMode ? (
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className={selectClasses}
              >
                <option value="" disabled>
                  Select task type
                </option>
                {Object.entries(types).map(([key, label]) => (
                  <option key={key} value={key}>
                    {(label as string).capitalizeWords?.() ?? label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
                <span className="text-gray-800 font-medium">
                  {currentTask?.type?.capitalizeWords?.() ?? currentTask?.type}
                </span>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700 block mb-2">
              Estimated Time
            </label>
            <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
              <span className="text-gray-800 ">
                {currentTask?.approxTime || "Not specified"}
              </span>
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-700 block mb-2">
            Description
          </label>
          {editMode ? (
            <TiptapEditor form={form} setForm={setForm} />
          ) : (
            <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
              <div
                className="text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: currentTask?.description || "No description provided",
                }}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-700 block">
              Priority Level
            </label>
            {editMode ? (
              <select
                name="priority"
                value={form.priority || ""}
                onChange={handleChange}
                className={selectClasses}
              >
                <option value="" disabled>
                  Select priority level
                </option>
                {Object.entries(priorities).map(([key, label]) => (
                  <option key={key} value={key}>
                    {(label as string).capitalizeWords?.() ?? label}
                  </option>
                ))}
              </select>
            ) : (
              <span
                className={`text-xs font-medium ${getPriorityColor(
                  currentTask?.priority ?? ""
                )}`}
              >
                {currentTask?.priority?.capitalizeWords?.() ??
                  currentTask?.priority}
              </span>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Timeline</h2>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-2">
                  Start Date
                </label>
                {editMode ? (
                  <input
                    name="startDate"
                    type="date"
                    value={formatDateForInput(form.startDate ?? "")}
                    onChange={handleChange}
                    className={inputBaseClasses}
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-gray-800">
                      {formatDate(currentTask?.startDate ?? null)}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-700 block mb-2">
                  End Date
                </label>
                {editMode ? (
                  <input
                    name="endDate"
                    type="date"
                    value={formatDateForInput(form.endDate ?? "")}
                    onChange={handleChange}
                    className={inputBaseClasses}
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                    <span className="text-gray-800">
                      {formatDate(currentTask?.endDate ?? null)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <label className="text-xs font-medium text-gray-700 block">
                Completion Date
              </label>

              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                <span className="text-gray-800">
                  {formatDate(
                    "completionDate" in (currentTask ?? {})
                      ? (currentTask as ITaskManagement).completionDate ?? null
                      : null
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Members Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-4">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Team Members</h2>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-3">
                Assignee
              </label>
              {editMode ? (
                <UserSelectorSearch
                  users={users}
                  label="Assignee"
                  value={form.assignee}
                  onChange={(val) =>
                    handleChange({
                      target: { name: "assignee", value: val },
                    } as any)
                  }
                />
              ) : (
                <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-medium">
                      {(currentTask?.assignee as unknown as IUserRef)?.username
                        ?.charAt(0)
                        ?.toUpperCase() ?? ""}
                    </span>
                  </div>
                  <span className="text-blue-800 font-medium">
                    {(
                      currentTask?.assignee as unknown as IUserRef
                    )?.username?.capitalizeWords?.() ??
                      (currentTask?.assignee as unknown as IUserRef)?.username}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 block mb-3">
                Created By
              </label>
              <div className="flex items-center gap-3 p-2 bg-purple-50 rounded-lg border border-purple-200">
                <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-medium">
                    {(currentTask?.author as unknown as IUserRef)?.username
                      ?.charAt(0)
                      ?.toUpperCase() ?? ""}
                  </span>
                </div>
                <span className="text-purple-800 font-medium">
                  {(
                    currentTask?.author as unknown as IUserRef
                  )?.username?.capitalizeWords?.() ??
                    (currentTask?.author as unknown as IUserRef)?.username}
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700 block mb-3">
              Followers ({currentTask?.followers?.length ?? 0})
            </label>

            {editMode ? (
              <UserSelectorSearch
                users={users}
                label="Followers"
                value={form.followers}
                onChange={(val) =>
                  handleChange({
                    target: { name: "followers", value: val },
                  } as any)
                }
                multiple
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {currentTask?.followers?.map((follower: any) => (
                  <div
                    key={follower._id}
                    className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 transition-colors p-2 rounded-md"
                  >
                    <div className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-medium">
                        {follower?.username?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-gray-700 text-xs font-medium">
                      {follower?.username?.capitalizeWords?.() ??
                        follower?.username}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Repetitive Task Section */}
          {(currentTask as IRepetitiveTaskManager)?.typeOfRepetition && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-4">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-sm font-semibold text-gray-900">
                  Repetitive Task
                </h2>
              </div>

              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-2">
                        <h2 className="text-blue-800 font-bold text-sm">
                          Repetition
                        </h2>
                      </div>

                      {editMode ? (
                        <select
                          value={repetitionType}
                          onChange={(e) =>
                            handleRepetitionTypeChange(
                              e.target.value as RepetitiveTaskType
                            )
                          }
                          className={selectClasses}
                        >
                          {Object.values(RepetitiveTaskType).map((val) => (
                            <option key={val} value={val}>
                              {val.capitalizeWords()}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div>
                          <span className="items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold border border-blue-200">
                            {(
                              currentTask as IRepetitiveTaskManager
                            )?.typeOfRepetition?.type?.capitalizeWords?.() ??
                              (currentTask as IRepetitiveTaskManager)
                                ?.typeOfRepetition?.type}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Repetition Details */}
                    {editMode ? (
                      <div className="bg-white/70 rounded-lg p-4 border border-blue-100 space-y-4">
                        {repetitionType === RepetitiveTaskType.weekly && (
                          <div>
                            <label className={labelClasses}>
                              Repeat on Days
                            </label>

                            {/* Daily Checkbox */}
                            <label className="flex items-center gap-2 mb-2">
                              <input
                                type="checkbox"
                                checked={
                                  selectedDays.length ===
                                  Object.values(RepetitiveTaskDays).filter(
                                    (d) => d !== RepetitiveTaskDays.sunday
                                  ).length
                                }
                                onChange={(e) =>
                                  handleDaySelectionAll(e.target.checked)
                                }
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-xs font-medium text-gray-700">
                                Daily (Mon-Sat)
                              </span>
                            </label>

                            <div className="grid grid-cols-7 gap-2 mt-2">
                              {Object.values(RepetitiveTaskDays).map((day) => (
                                <label
                                  key={day}
                                  className={`flex items-center justify-center p-2 rounded-lg border border-gray-200 cursor-pointer transition-all ${
                                    selectedDays.includes(day)
                                      ? "bg-blue-100 border-blue-300 text-blue-700"
                                      : "bg-white text-gray-700 hover:bg-gray-50"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedDays.includes(day)}
                                    onChange={() => handleDaySelection(day)}
                                    className="hidden"
                                  />
                                  <span className="text-xs font-medium">
                                    {day.charAt(0).toUpperCase() +
                                      day.slice(1, 3)}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {(repetitionType === RepetitiveTaskType.monthly ||
                          repetitionType === RepetitiveTaskType.yearly) && (
                          <div>
                            <label className={labelClasses}>
                              Select Date ({repetitionType})
                            </label>
                            <input
                              type="date"
                              value={
                                repetitionType === RepetitiveTaskType.monthly
                                  ? monthlyDate
                                  : yearlyDate
                              }
                              onChange={(e) =>
                                repetitionType === RepetitiveTaskType.monthly
                                  ? setMonthlyDate(e.target.value)
                                  : setYearlyDate(e.target.value)
                              }
                              className={inputBaseClasses}
                            />
                          </div>
                        )}

                        <div>
                          <label className={labelClasses}>
                            Number of Days (Optional)
                          </label>
                          <input
                            type="number"
                            value={noOfDays}
                            onChange={(e) =>
                              setNoOfDays(Number(e.target.value) || 0)
                            }
                            className={inputBaseClasses}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white/70 rounded-lg p-2 border border-blue-100">
                        {formatRepetitionDetails(
                          (currentTask as IRepetitiveTaskManager)
                            ?.typeOfRepetition
                        )}
                        <div className="flex items-center">
                          <div className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                            Added Dates:
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {addedDatesEntries.map(([dateKey]) => (
                              <span
                                key={dateKey}
                                className="px-2 py-1 bg-gray-100 rounded-md text-xs text-slate-700"
                              >
                                {dateKey}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="hidden sm:flex flex-col items-end gap-1">
                    {(currentTask as IRepetitiveTaskManager)?.typeOfRepetition
                      ?.data?.days?.length ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {
                          (currentTask as IRepetitiveTaskManager)
                            ?.typeOfRepetition?.data?.days?.length
                        }{" "}
                        day(s)
                      </span>
                    ) : (currentTask as IRepetitiveTaskManager)
                        ?.typeOfRepetition?.data?.date ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {
                          (currentTask as IRepetitiveTaskManager)
                            ?.typeOfRepetition?.data?.date
                        }
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center mt-2 gap-3"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsPanel;
